import apiClient, { type PagedResponse } from '../client'
import type { CompanyPersonResponse, CompanyDocumentResponse, DocumentRequirementResponse } from '../models/response'
import type { CarrierListItemResponse, TransportistaResponse, TruckResponse, DriverResponse, IncompleteTruckResponse, CarrierValidationResponse, CarrierComplianceResponse, OfacHitResponse, CarrierStepApprovalEntryResponse, CarrierStepApprovalResponse } from '../models/response/transportista'
import type { CreateTransportistaStep1Request, CreateTruckRequest, CreateDriverRequest, RegularizeTruckRequest, SaveCarrierSustainabilityRequest } from '../models/request/transportista'
import type { AddCompanyPersonRequest } from '../models/request/company'

const BASE_PATH = '/transportistas'

export const transportistasService = {
  async getAll(params?: {
    page?: number
    pageSize?: number
    search?: string
  }): Promise<PagedResponse<CarrierListItemResponse>> {
    return apiClient.get<PagedResponse<CarrierListItemResponse>>(BASE_PATH, params)
  },

  async getById(id: string): Promise<TransportistaResponse> {
    return apiClient.get<TransportistaResponse>(`${BASE_PATH}/${id}`)
  },

  async getPending(): Promise<TransportistaResponse> {
    return apiClient.get<TransportistaResponse>(`${BASE_PATH}/pending`)
  },

  async createStep1(data: CreateTransportistaStep1Request): Promise<TransportistaResponse> {
    return apiClient.post<TransportistaResponse, CreateTransportistaStep1Request>(`${BASE_PATH}/step1`, data)
  },

  async addPerson(id: string, data: AddCompanyPersonRequest): Promise<CompanyPersonResponse> {
    return apiClient.post<CompanyPersonResponse, AddCompanyPersonRequest>(`${BASE_PATH}/${id}/step2/persons`, data)
  },

  async removePerson(id: string, companyPersonId: string): Promise<void> {
    return apiClient.delete<void>(`${BASE_PATH}/${id}/step2/persons/${companyPersonId}`)
  },

  async updateStep3(id: string, data: CreateTruckRequest[]): Promise<TruckResponse[]> {
    return apiClient.put<TruckResponse[], CreateTruckRequest[]>(`${BASE_PATH}/${id}/step3`, data)
  },

  async updateStep4(id: string, data: CreateDriverRequest[]): Promise<DriverResponse[]> {
    return apiClient.put<DriverResponse[], CreateDriverRequest[]>(`${BASE_PATH}/${id}/step4`, data)
  },

  async uploadDocument(id: string, data: { companyBusinessId: string; documentTypeId: number; documentUuid?: string; fileName?: string; expiresAt?: string; notes?: string }): Promise<CompanyDocumentResponse> {
    return apiClient.post<CompanyDocumentResponse>(`${BASE_PATH}/${id}/step5/documents`, data)
  },

  async finalize(id: string): Promise<void> {
    return apiClient.post<void>(`${BASE_PATH}/${id}/finalize`)
  },

  // Homologación
  async submitForReview(id: string): Promise<void> {
    return apiClient.post<void>(`${BASE_PATH}/${id}/submit`)
  },

  async homologate(id: string): Promise<void> {
    return apiClient.post<void>(`${BASE_PATH}/${id}/homologate`)
  },

  async addObservation(id: string, step: number, observationText: string): Promise<void> {
    return apiClient.post<void, { step: number; observationText: string }>(`${BASE_PATH}/${id}/observations`, { step, observationText })
  },

  async resolveObservation(id: string, observationId: string): Promise<void> {
    return apiClient.put<void, undefined>(`${BASE_PATH}/${id}/observations/${observationId}/resolve`, undefined)
  },

  async addObservationReply(id: string, observationId: string, replyText: string): Promise<void> {
    return apiClient.post<void, { replyText: string }>(`${BASE_PATH}/${id}/observations/${observationId}/replies`, { replyText })
  },

  // Aprobación por paso/tab (evaluación)
  async getStepApprovals(id: string): Promise<CarrierStepApprovalEntryResponse[]> {
    return apiClient.get<CarrierStepApprovalEntryResponse[]>(`${BASE_PATH}/${id}/step-approvals`)
  },

  async approveStep(id: string, step: number): Promise<CarrierStepApprovalResponse> {
    return apiClient.post<CarrierStepApprovalResponse>(`${BASE_PATH}/${id}/steps/${step}/approve`)
  },

  // Vehículos por regularizar (creados desde la app de traslado)
  async listIncompleteTrucks(): Promise<IncompleteTruckResponse[]> {
    return apiClient.get<IncompleteTruckResponse[]>(`${BASE_PATH}/incomplete-trucks`)
  },

  async regularizeTruck(truckId: string, data: RegularizeTruckRequest): Promise<void> {
    return apiClient.put<void, RegularizeTruckRequest>(`${BASE_PATH}/trucks/${truckId}/regularize`, data)
  },

  // Validaciones externas (F5)
  async getValidations(id: string): Promise<CarrierValidationResponse[]> {
    return apiClient.get<CarrierValidationResponse[]>(`${BASE_PATH}/${id}/validations`)
  },

  async runValidations(id: string): Promise<CarrierValidationResponse[]> {
    return apiClient.post<CarrierValidationResponse[]>(`${BASE_PATH}/${id}/validations/run`)
  },

  // Compliance OCDE/LBMA (F6)
  async getCompliance(id: string): Promise<CarrierComplianceResponse> {
    return apiClient.get<CarrierComplianceResponse>(`${BASE_PATH}/${id}/compliance`)
  },

  async saveSustainability(id: string, data: SaveCarrierSustainabilityRequest): Promise<void> {
    return apiClient.put<void, SaveCarrierSustainabilityRequest>(`${BASE_PATH}/${id}/sustainability`, data)
  },

  async screen(id: string): Promise<OfacHitResponse[]> {
    return apiClient.post<OfacHitResponse[]>(`${BASE_PATH}/${id}/screen`)
  },

  async syncOfac(): Promise<number> {
    return apiClient.post<number>(`${BASE_PATH}/ofac/sync`)
  },

  async getDocumentRequirements(): Promise<DocumentRequirementResponse[]> {
    return apiClient.get<DocumentRequirementResponse[]>(`${BASE_PATH}/document-requirements`)
  },
}
